import React, { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from "sonner";
import { getUserErrorMessage } from '@/lib/error-messages';

const API_BASE_URL = `${window.location.origin}/api`;
const USER_PROFILE_COOKIE = 'hiredcoach-user';
const ACCESS_TOKEN_COOKIE = 'hiredcoach-token';

interface User {
  id: string;
  email: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  honorific?: string;
  avatar_mode?: 'none' | 'preset' | 'upload';
  avatar_preset?: string | null;
  avatar_url?: string | null;
  isAdmin?: boolean;
  // Subscription snapshot fields from backend /auth endpoints
  subscription_mode?: 'free' | 'premium';
  subscription_expires_at?: string | null;
  v1_privacy_agreed_on?: string | null;
  v1_user_agreement_agreed_on?: string | null;
  agreements_v1?: boolean;
  linkedin_connected?: boolean;
  linkedin_connected_at?: string | null;
  one_week_offer_seen_at?: string | null;
  one_week_subscription_used_at?: string | null;
  one_week_subscription_used?: boolean;
  one_week_offer_eligible?: boolean;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  login: (email: string, password: string, returnUrl?: string) => Promise<void>;
  register: (
    email: string,
    password: string,
    firstName?: string,
    lastName?: string,
    middleName?: string,
    honorific?: string
  ) => Promise<void>;
  completeOauthLogin: (accessToken: string, returnUrl?: string) => Promise<void>;
  logout: (returnUrl?: string) => void;
  refreshAuthSnapshot: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Check for saved token and user data
    const savedToken = localStorage.getItem(ACCESS_TOKEN_COOKIE);
    const savedUser = localStorage.getItem(USER_PROFILE_COOKIE);

    if (savedToken && savedUser) {
      setToken(savedToken);
      setUser(JSON.parse(savedUser));
      // Refresh from backend so signed links (like avatar URLs) stay current.
      hydrateFromToken(savedToken).catch(() => {
        localStorage.removeItem(ACCESS_TOKEN_COOKIE);
        localStorage.removeItem(USER_PROFILE_COOKIE);
        setToken(null);
        setUser(null);
      });
    }

    setIsLoading(false);
  }, []);

  const normalizeUser = (me: any): User => ({
    id: me.id,
    email: me.email,
    firstName: me.first_name,
    middleName: me.middle_name,
    lastName: me.last_name,
    honorific: me.honorific,
    avatar_mode: me.avatar_mode || 'none',
    avatar_preset: me.avatar_preset ?? null,
    avatar_url: me.avatar_url ?? null,
    isAdmin: me.is_admin || false,
    subscription_mode: me.subscription_mode,
    subscription_expires_at: me.subscription_expires_at ?? null,
    v1_privacy_agreed_on: me.v1_privacy_agreed_on ?? null,
    v1_user_agreement_agreed_on: me.v1_user_agreement_agreed_on ?? null,
    agreements_v1: me.agreements_v1 === true,
    linkedin_connected: me.linkedin_connected === true,
    linkedin_connected_at: me.linkedin_connected_at ?? null,
    one_week_offer_seen_at: me.one_week_offer_seen_at ?? null,
    one_week_subscription_used_at: me.one_week_subscription_used_at ?? null,
    one_week_subscription_used: me.one_week_subscription_used === true,
    one_week_offer_eligible: me.one_week_offer_eligible === true,
  });

  const hydrateFromToken = async (accessToken: string): Promise<User> => {
    const meResp = await fetch(`${API_BASE_URL}/auth/me`, {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
    });
    if (!meResp.ok) {
      throw new Error('Failed to fetch user data');
    }
    const me = await meResp.json();
    const normalizedUser = normalizeUser(me);

    localStorage.setItem(ACCESS_TOKEN_COOKIE, accessToken);
    localStorage.setItem(USER_PROFILE_COOKIE, JSON.stringify(normalizedUser));

    setToken(accessToken);
    setUser(normalizedUser);
    return normalizedUser;
  };

  const refreshAuthSnapshot = async () => {
    try {
      const currentToken = localStorage.getItem(ACCESS_TOKEN_COOKIE);
      if (!currentToken) return;

      // Refresh token embedding latest subscription snapshot
      const refreshResp = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${currentToken}`,
        },
      });
      if (!refreshResp.ok) {
        // If refresh fails, bail out silently
        return;
      }
      const refreshData = await refreshResp.json();
      const newToken: string | undefined = refreshData.access_token;
      if (!newToken) return;

      await hydrateFromToken(newToken);
    } catch (e) {
      // No-op on error
      console.error('Failed to refresh auth snapshot', e);
    }
  };

  const register = async (
    email: string,
    password: string,
    firstName?: string,
    lastName?: string,
    middleName?: string,
    honorific?: string
  ) => {
    try {
      setIsLoading(true);
      const response = await fetch(`${API_BASE_URL}/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
          first_name: firstName || null,
          middle_name: middleName || null,
          last_name: lastName || null,
          honorific: honorific || null,
          accept_agreements: true,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const msg = response.status === 409
          ? 'An account with this email already exists.'
          : getUserErrorMessage(errorData?.detail, 'Unable to create account. Please try again later.');
        throw new Error(msg);
      }

      const tokenData = await response.json();
      const accessToken = tokenData.access_token;

      await hydrateFromToken(accessToken);

      toast.success("Welcome! Let's get started...");
      navigate('/dashboard');
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Unable to create account. Please try again later.'));
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string, returnUrl?: string) => {
    try {
      setIsLoading(true);

      // In a real implementation, this would be a token endpoint call
      // For demo purposes, we'll simulate a successful login
      const tokenResponse = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          password: password,
        })
      });

      if (!tokenResponse.ok) {
        const errorData = await tokenResponse.json().catch(() => ({}));
        const msg = tokenResponse.status === 401
          ? 'Invalid email or password.'
          : getUserErrorMessage(errorData?.detail, 'Login failed. Please try again later.');
        throw new Error(msg);
      }

      const tokenData = await tokenResponse.json();
      const accessToken = tokenData.access_token;

      await hydrateFromToken(accessToken);

      toast.success('Login successful!');
      
      // Redirect to return URL if provided, otherwise default to dashboard
      const redirectTo = returnUrl || '/dashboard';
      navigate(redirectTo);
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Login failed. Please try again later.'));
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const completeOauthLogin = async (accessToken: string, returnUrl?: string) => {
    try {
      setIsLoading(true);
      await hydrateFromToken(accessToken);
      const redirectTo = returnUrl || '/dashboard';
      navigate(redirectTo);
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'LinkedIn login failed. Please try again later.'));
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = (returnUrl?: string) => {
    localStorage.removeItem(ACCESS_TOKEN_COOKIE);
    localStorage.removeItem(USER_PROFILE_COOKIE);
    setToken(null);
    setUser(null);
    
    // Build login URL with return URL parameter
    const loginPath = returnUrl 
      ? `/login?returnUrl=${encodeURIComponent(returnUrl)}`
      : '/login';
    navigate(loginPath);
    toast.info('You have been logged out');
  };

  const value = {
    user,
    token,
    isLoading,
    login,
    register,
    completeOauthLogin,
    logout,
    refreshAuthSnapshot,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
